import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Crown, Users, TrendingUp, RefreshCw, Power, PowerOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromotionManager = () => {
  const [promotions, setPromotions] = useState([]);
  const [foundingMembers, setFoundingMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/promotions`, {
        withCredentials: true
      });
      setPromotions(response.data.promotions || []);
      setFoundingMembers(response.data.founding_members || []);
    } catch (error) {
      console.error("Error fetching promotions:", error);
      toast.error("Failed to load promotions");
    } finally {
      setLoading(false);
    }
  };

  const togglePromotion = async (promotionId, currentStatus) => {
    try {
      await axios.post(
        `${API}/admin/promotions/toggle?promotion_id=${promotionId}&is_active=${!currentStatus}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Promotion ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      console.error("Error toggling promotion:", error);
      toast.error("Failed to toggle promotion");
    }
  };

  const foundingPromo = promotions.find(p => p.promotion_type === "founding_members");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Promotions Manager</h2>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600"></div>
        </div>
      ) : (
        <>
          {/* Founding Members Promotion */}
          {foundingPromo && (
            <Card className="p-6 border-2 border-orange-500">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg">
                    <Crown className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{foundingPromo.name}</h3>
                    <p className="text-sm text-gray-600">
                      Created: {new Date(foundingPromo.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => togglePromotion(foundingPromo.id, foundingPromo.is_active)}
                  variant={foundingPromo.is_active ? "destructive" : "default"}
                  size="sm"
                >
                  {foundingPromo.is_active ? (
                    <><PowerOff className="w-4 h-4 mr-2" /> Deactivate</>
                  ) : (
                    <><Power className="w-4 h-4 mr-2" /> Activate</>
                  )}
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Total Spots</div>
                  <div className="text-3xl font-bold text-blue-600">
                    {foundingPromo.total_spots}
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Claimed</div>
                  <div className="text-3xl font-bold text-green-600">
                    {foundingPromo.claimed_spots}
                  </div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Remaining</div>
                  <div className="text-3xl font-bold text-orange-600">
                    {foundingPromo.total_spots - foundingPromo.claimed_spots}
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Status</div>
                  <div className={`text-lg font-bold ${foundingPromo.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                    {foundingPromo.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Claim Progress</span>
                  <span className="text-sm font-bold">
                    {Math.round((foundingPromo.claimed_spots / foundingPromo.total_spots) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className="bg-gradient-to-r from-yellow-400 to-orange-500 h-4 rounded-full transition-all duration-500"
                    style={{ width: `${(foundingPromo.claimed_spots / foundingPromo.total_spots) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-bold mb-2">Benefits:</h4>
                <ul className="space-y-1 text-sm">
                  {foundingPromo.benefits.lifetime_free_basic && (
                    <li>✅ Lifetime FREE basic listings</li>
                  )}
                  {foundingPromo.benefits.featured_tier_days && (
                    <li>✅ Featured tier FREE for {foundingPromo.benefits.featured_tier_days} days</li>
                  )}
                  {foundingPromo.benefits.never_expires && (
                    <li>✅ Listings never expire</li>
                  )}
                  {foundingPromo.benefits.founding_member_badge && (
                    <li>✅ Founding Member badge</li>
                  )}
                </ul>
              </div>
            </Card>
          )}

          {/* Founding Members List */}
          <Card className="p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center">
              <Users className="w-6 h-6 mr-2 text-orange-600" />
              Founding Members ({foundingMembers.length})
            </h3>

            {foundingMembers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No founding members yet. Waiting for first signups...
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Joined
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {foundingMembers.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <Crown className="w-4 h-4 text-yellow-500 mr-2" />
                            <span className="font-bold text-orange-600">
                              #{member.founding_member_number}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium">{member.name}</td>
                        <td className="px-4 py-3 text-gray-600">{member.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(member.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default PromotionManager;
